import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Storage, Translate } from 'react-jhipster';
import { useAppSelector } from 'app/config/store';
import { RouteComponentProps } from 'react-router-dom';
import { AUTHORITIES } from 'app/config/constants';
import { KORI_ORANGE } from 'app/entities/notes/bulletins.utils';
import './controle-acces.scss';
import {
  Alert,
  Avatar,
  Button,
  Card,
  Checkbox,
  Col,
  Empty,
  Form,
  Input,
  List,
  Modal,
  Pagination,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { EyeOutlined, PlusOutlined, SearchOutlined, SafetyCertificateOutlined, TeamOutlined } from '@ant-design/icons';

const apiUrl = 'api/controle-acces';
const PAGE_SIZE = 10;

type Option = { id: string; code: string; libelle: string; actif: boolean };
type Classe = { id: string; nom: string; cycleNom?: string };
type InscriptionRow = {
  inscriptionId: string;
  eleveId: string;
  nom: string;
  prenom: string;
  matricule: string;
  classeId: string;
  classeName?: string;
  droitActif: boolean;
};

interface IControleAccesRouteParams {
  ecoleId?: string;
  anneeScolaireId?: string;
}

export const ControleAcces = (props: RouteComponentProps<IControleAccesRouteParams>) => {
  const [options, setOptions] = useState<Option[]>([]);
  const [classes, setClasses] = useState<Classe[]>([]);
  const [selected, setSelected] = useState<Option | null>(null);
  const [selectedClassId, setSelectedClassId] = useState<string>();
  const [schoolYearName, setSchoolYearName] = useState('');
  const [rows, setRows] = useState<InscriptionRow[]>([]);
  const [libelle, setLibelle] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<boolean | undefined>();
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [loadingRows, setLoadingRows] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [editingOption, setEditingOption] = useState<Option | null>(null);
  const [optionModalOpen, setOptionModalOpen] = useState(false);
  const [optionForm] = Form.useForm<{ libelle: string }>();
  const authorities = useAppSelector(state => state.authentication.account.authorities);
  const currentEcole = useAppSelector(state => state.ecole.entity);
  const mayManage = authorities?.some(authority => [AUTHORITIES.ADMIN, AUTHORITIES.DIRECTEUR].includes(authority));
  const ecoleId = props.match.params.ecoleId;
  const yearId = props.match.params.anneeScolaireId;
  const storedEcole: any = Storage.session.get('currentEcole');
  const storedSchoolYear: any = Storage.session.get('currentAnneeScolaire');
  const storedSchoolYearId = storedSchoolYear?.id || storedSchoolYear;
  const contextReady = Boolean(ecoleId && yearId);
  const selectedClass = classes.find(classe => classe.id === selectedClassId);

  useEffect(() => {
    if (!yearId) {
      setSchoolYearName('');
      return undefined;
    }

    let active = true;
    const cachedSchoolYearName = String(storedSchoolYear?.id) === String(yearId) ? storedSchoolYear?.nom : undefined;
    setSchoolYearName(cachedSchoolYearName || '');
    axios
      .get<{ nom?: string }>(`api/annees-scolaires/${encodeURIComponent(yearId)}`)
      .then(response => {
        if (active) setSchoolYearName(response.data.nom || cachedSchoolYearName || '');
      })
      .catch(() => {
        if (active) setSchoolYearName(cachedSchoolYearName || '');
      });

    return () => {
      active = false;
    };
  }, [yearId, storedSchoolYear?.id, storedSchoolYear?.nom]);

  useEffect(() => {
    if (ecoleId && yearId) return;
    const schoolId = ecoleId || currentEcole?.id || storedEcole?.id;
    const schoolYearId = yearId || storedSchoolYearId;
    if (schoolId && schoolYearId) {
      props.history.replace(`/controle-acces/${encodeURIComponent(schoolId)}/${encodeURIComponent(schoolYearId)}`);
    }
  }, [ecoleId, yearId, currentEcole?.id, storedEcole?.id, storedSchoolYearId, props.history]);

  const loadOptions = useCallback(async () => {
    if (!ecoleId || !yearId) return;
    setLoadingOptions(true);
    try {
      const response = await axios.get<Option[]>(`${apiUrl}/options`, { params: { ecoleId } });
      setOptions(response.data);
      setSelected(current => (current ? response.data.find(option => option.id === current.id) || null : response.data[0] || null));
      try {
        const classesResponse = await axios.get<Classe[]>('api/classes', { params: { ecoleId } });
        setClasses(classesResponse.data);
      } catch (_) {
        setClasses([]);
        setError('Impossible de charger les classes de cette école.');
      }
    } catch (_) {
      setError('Impossible de charger les options de contrôle d’accès.');
    } finally {
      setLoadingOptions(false);
    }
  }, [ecoleId, yearId]);

  const loadRows = useCallback(async () => {
    if (!selected || !yearId || !selectedClassId) {
      setRows([]);
      setTotal(0);
      setSelectedIds([]);
      return;
    }
    setLoadingRows(true);
    try {
      const params: any = {
        anneeScolaireId: yearId,
        classeId: selectedClassId,
        page,
        size: PAGE_SIZE,
        recherche: search || undefined,
        droitActif: status,
      };
      const response = await axios.get(`${apiUrl}/options/${selected.id}/inscriptions`, { params });
      setRows(response.data.content || []);
      setTotal(response.data.totalElements || 0);
      setSelectedIds([]);
    } catch (_) {
      setError('Impossible de charger les inscriptions pour cette option.');
    } finally {
      setLoadingRows(false);
    }
  }, [selected, yearId, selectedClassId, page, search, status]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);
  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const openClass = (classe: Classe) => {
    setSelectedClassId(classe.id);
    setPage(0);
    setSearch('');
    setStatus(undefined);
    setSelectedIds([]);
  };

  const openOptionModal = (option?: Option) => {
    setEditingOption(option || null);
    optionForm.setFieldsValue({ libelle: option?.libelle || '' });
    setOptionModalOpen(true);
  };

  const saveOption = async ({ libelle: value }: { libelle: string }) => {
    setSaving(true);
    setError('');
    try {
      if (editingOption) {
        await axios.patch(`${apiUrl}/options/${editingOption.id}`, { libelle: value.trim() });
        message.success('Le nom de l’option a été modifié.');
      } else {
        await axios.post(`${apiUrl}/options`, { libelle: value.trim(), actif: true }, { params: { ecoleId } });
        message.success('L’option a été créée.');
      }
      setOptionModalOpen(false);
      optionForm.resetFields();
      await loadOptions();
    } catch (_) {
      setError(
        editingOption ? 'Le nom de l’option n’a pas pu être modifié.' : 'L’option n’a pas pu être créée. Vérifiez le nom puis réessayez.'
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleOption = async (option: Option, actif: boolean) => {
    try {
      await axios.patch(`${apiUrl}/options/${option.id}`, { actif });
      setOptions(current => current.map(item => (item.id === option.id ? { ...item, actif } : item)));
      setSelected(current => (current?.id === option.id ? { ...current, actif } : current));
      message.success(actif ? 'Option activée pour l’école.' : 'Option désactivée pour l’école.');
    } catch (_) {
      setError('Impossible de modifier cette option. Le réglage précédent a été conservé.');
    }
  };

  const toggleDroit = async (row: InscriptionRow, actif: boolean) => {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.patch(`${apiUrl}/options/${selected.id}/inscriptions/${row.inscriptionId}`, { actif });
      setRows(current => current.map(item => (item.inscriptionId === row.inscriptionId ? { ...item, droitActif: actif } : item)));
    } catch (_) {
      setError(`Le droit de ${row.prenom} ${row.nom} n’a pas pu être modifié.`);
    } finally {
      setSaving(false);
    }
  };

  const applyToSelected = async (actif: boolean) => {
    if (!selected || selectedIds.length === 0) return;
    setSaving(true);
    try {
      await axios.patch(`${apiUrl}/options/${selected.id}/inscriptions/lot`, { inscriptionIds: selectedIds, actif });
      setRows(current => current.map(row => (selectedIds.includes(row.inscriptionId) ? { ...row, droitActif: actif } : row)));
      setSelectedIds([]);
      message.success(`Droits ${actif ? 'accordés' : 'retirés'} pour ${selectedIds.length} élève(s).`);
    } catch (_) {
      setError('La mise à jour groupée a échoué. La liste a été rechargée pour refléter les droits enregistrés.');
      await loadRows();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid controle-acces-page">
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col>
            <Space align="start">
              <SafetyCertificateOutlined style={{ fontSize: 28, color: KORI_ORANGE, marginTop: 6 }} />
              <div>
                <Typography.Title level={2} style={{ margin: 0 }}>
                  <Translate contentKey="controleAcces.title">Contrôle d’accès</Translate>
                </Typography.Title>
                <Typography.Text type="secondary">Gérez les options de l’école et les autorisations de chaque élève.</Typography.Text>
              </div>
            </Space>
          </Col>
          {mayManage && (
            <Col>
              <Button type="primary" icon={<PlusOutlined />} disabled={!contextReady} onClick={() => openOptionModal()}>
                Nouvelle option
              </Button>
            </Col>
          )}
        </Row>

        {error && <Alert type="error" showIcon closable message={error} onClose={() => setError('')} />}
        {!contextReady && (
          <Alert
            type="warning"
            showIcon
            message="École ou année scolaire absente de l’URL"
            description="Ouvrez le contrôle d’accès depuis le menu de l’année scolaire pour travailler sur le bon contexte."
          />
        )}

        <Spin spinning={loadingOptions}>
          {options.length === 0 ? (
            <Card>
              <Empty description="Aucune option configurée pour cette école.">
                {mayManage && (
                  <Button type="primary" icon={<PlusOutlined />} disabled={!contextReady} onClick={() => openOptionModal()}>
                    Créer la première option
                  </Button>
                )}
              </Empty>
            </Card>
          ) : (
            <List
              grid={{ gutter: 12, xs: 1, sm: 2, md: 3, lg: 4, xl: 5 }}
              dataSource={options}
              renderItem={option => (
                <List.Item>
                  <Card
                    hoverable
                    size="small"
                    onClick={() => {
                      setSelected(option);
                      setSelectedClassId(undefined);
                      setPage(0);
                      setSelectedIds([]);
                    }}
                    style={{
                      width: '100%',
                      borderColor: selected?.id === option.id ? KORI_ORANGE : undefined,
                      boxShadow: selected?.id === option.id ? '0 0 0 2px rgba(242,142,25,.16)' : undefined,
                    }}
                    title={
                      <Typography.Text strong ellipsis>
                        {option.libelle}
                      </Typography.Text>
                    }
                    extra={option.actif ? <Tag color="green">Active</Tag> : <Tag>Désactivée</Tag>}
                  >
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Typography.Text type="secondary">
                        {option.actif ? 'Les scans sont autorisés selon les droits des élèves.' : 'Aucun élève ne peut passer au scan.'}
                      </Typography.Text>
                      <Space onClick={event => event.stopPropagation()}>
                        {mayManage && (
                          <>
                            <Switch
                              size="small"
                              checked={option.actif}
                              onChange={checked => toggleOption(option, checked)}
                              aria-label={`Activer ${option.libelle}`}
                            />
                            <Button type="link" size="small" onClick={() => openOptionModal(option)}>
                              Renommer
                            </Button>
                          </>
                        )}
                      </Space>
                    </Space>
                  </Card>
                </List.Item>
              )}
            />
          )}
        </Spin>

        {selected && (
          <Card
            title={
              <Space direction="vertical" size={0}>
                <Typography.Title level={4} style={{ margin: 0 }}>
                  {selected.libelle}
                </Typography.Title>
                <Typography.Text type="secondary">
                  Année scolaire : {schoolYearName || (yearId ? 'Chargement…' : 'non définie')}
                </Typography.Text>
              </Space>
            }
          >
            {!yearId ? (
              <Empty description="Sélectionnez une année scolaire pour afficher les inscriptions." />
            ) : (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {!selected.actif && (
                  <Alert
                    type="warning"
                    showIcon
                    message="Cette option est désactivée pour toute l’école."
                    description="Les droits individuels sont conservés, mais aucun scan ne sera accepté tant que l’option reste désactivée."
                  />
                )}
                <Row justify="space-between" align="middle" gutter={[12, 12]}>
                  <Col>
                    <Typography.Title level={5} style={{ margin: 0 }}>
                      Classes
                    </Typography.Title>
                    <Typography.Text type="secondary">Ouvrez une classe pour consulter ses élèves et gérer leurs droits.</Typography.Text>
                  </Col>
                  <Col>
                    <Tag color={KORI_ORANGE} icon={<TeamOutlined />}>
                      {classes.length} classe(s)
                    </Tag>
                  </Col>
                </Row>
                <Spin spinning={loadingOptions}>
                  {classes.length === 0 ? (
                    <Empty description="Aucune classe disponible pour cette école." />
                  ) : (
                    <Table
                      size="small"
                      rowKey="id"
                      className="controle-acces-classe-table"
                      dataSource={[...classes].sort((a, b) => a.nom.localeCompare(b.nom, 'fr'))}
                      pagination={false}
                      onRow={(classe: Classe) => ({
                        onClick: () => openClass(classe),
                        style: { cursor: 'pointer' },
                      })}
                      columns={[
                        {
                          title: 'Classe et cycle',
                          dataIndex: 'nom',
                          key: 'nom',
                          render: (nom, classe: Classe) => (
                            <Space size={14}>
                              <Avatar
                                shape="square"
                                size={44}
                                icon={<TeamOutlined />}
                                style={{ backgroundColor: '#fff3e0', color: KORI_ORANGE, borderRadius: 12 }}
                              />
                              <Space direction="vertical" size={0}>
                                <Typography.Text strong style={{ fontSize: 15 }}>
                                  {nom}
                                </Typography.Text>
                                <Typography.Text type="secondary">
                                  {classe.cycleNom ? `Cycle ${classe.cycleNom}` : `Année ${schoolYearName || 'scolaire sélectionnée'}`}
                                </Typography.Text>
                              </Space>
                            </Space>
                          ),
                        },
                      ]}
                    />
                  )}
                </Spin>
              </Space>
            )}
          </Card>
        )}
      </Space>

      <Modal
        title={
          <Space size={12}>
            <Avatar
              shape="square"
              size={42}
              icon={<TeamOutlined />}
              style={{ backgroundColor: '#fff3e0', color: KORI_ORANGE, borderRadius: 12 }}
            />
            <Space direction="vertical" size={0}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                {selectedClass?.nom || 'Élèves de la classe'}
              </Typography.Title>
              <Typography.Text type="secondary">
                {selected?.libelle} · {schoolYearName || 'Année scolaire'}
              </Typography.Text>
            </Space>
          </Space>
        }
        className="controle-acces-eleves-modal"
        visible={Boolean(selectedClassId && selected)}
        onCancel={() => {
          setSelectedClassId(undefined);
          setSelectedIds([]);
        }}
        width={1000}
        footer={[
          <Button key="close" onClick={() => setSelectedClassId(undefined)}>
            Fermer
          </Button>,
        ]}
        destroyOnClose
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div className="controle-acces-eleves-filtres">
            <Row gutter={[12, 12]} align="middle">
              <Col xs={24} md={12}>
                <Input
                  allowClear
                  prefix={<SearchOutlined />}
                  placeholder="Rechercher un élève ou un matricule"
                  value={search}
                  onChange={event => {
                    setPage(0);
                    setSearch(event.target.value);
                  }}
                />
              </Col>
              <Col xs={16} md={8}>
                <Select
                  allowClear
                  placeholder="Tous les droits"
                  value={status}
                  onChange={value => {
                    setPage(0);
                    setStatus(value);
                  }}
                  style={{ width: '100%' }}
                  options={[
                    { value: true, label: 'Droits accordés' },
                    { value: false, label: 'Droits refusés' },
                  ]}
                />
              </Col>
              <Col xs={8} md={4}>
                <Typography.Text type="secondary">{total} élève(s)</Typography.Text>
              </Col>
            </Row>
          </div>

          {mayManage && selectedIds.length > 0 && (
            <Alert
              className="controle-acces-bulk-alert"
              type="info"
              showIcon
              message={`${selectedIds.length} élève(s) sélectionné(s)`}
              action={
                <Space>
                  <Button size="small" type="primary" disabled={!selected?.actif || saving} onClick={() => applyToSelected(true)}>
                    Accorder l’accès
                  </Button>
                  <Button size="small" danger disabled={saving} onClick={() => applyToSelected(false)}>
                    Retirer l’accès
                  </Button>
                </Space>
              }
            />
          )}

          <Spin spinning={loadingRows}>
            {rows.length === 0 ? (
              <Empty description="Aucun élève inscrit dans cette classe pour ces critères." />
            ) : (
              <Table
                size="small"
                scroll={{ x: 600 }}
                className="controle-acces-eleves-table"
                rowKey="inscriptionId"
                dataSource={rows}
                pagination={false}
                columns={[
                  ...(mayManage
                    ? [
                        {
                          title: (
                            <Checkbox
                              aria-label={`Sélectionner tous les élèves de ${selectedClass?.nom || 'la classe'}`}
                              checked={rows.every(row => selectedIds.includes(row.inscriptionId))}
                              indeterminate={
                                rows.some(row => selectedIds.includes(row.inscriptionId)) &&
                                !rows.every(row => selectedIds.includes(row.inscriptionId))
                              }
                              onChange={event =>
                                setSelectedIds(current =>
                                  event.target.checked
                                    ? Array.from(new Set([...current, ...rows.map(row => row.inscriptionId)]))
                                    : current.filter(id => !rows.some(row => row.inscriptionId === id))
                                )
                              }
                            />
                          ),
                          key: 'selection',
                          width: 48,
                          render: (_, row) => (
                            <Checkbox
                              aria-label={`Sélectionner ${row.prenom} ${row.nom}`}
                              checked={selectedIds.includes(row.inscriptionId)}
                              onChange={event =>
                                setSelectedIds(current =>
                                  event.target.checked ? [...current, row.inscriptionId] : current.filter(id => id !== row.inscriptionId)
                                )
                              }
                            />
                          ),
                        },
                      ]
                    : []),
                  {
                    title: 'Matricule',
                    dataIndex: 'matricule',
                    key: 'matricule',
                    width: 150,
                    render: value => (value ? <Tag>{value}</Tag> : <Typography.Text type="secondary">—</Typography.Text>),
                  },
                  {
                    title: 'Élève',
                    key: 'eleve',
                    render(_, row) {
                      const initials = `${row.prenom?.charAt(0) || ''}${row.nom?.charAt(0) || ''}`.toUpperCase();
                      return (
                        <Space size={10}>
                          <Avatar size={34} style={{ backgroundColor: '#fff3e0', color: KORI_ORANGE, fontWeight: 600 }}>
                            {initials}
                          </Avatar>
                          <Typography.Text strong>
                            {row.prenom} {row.nom}
                          </Typography.Text>
                        </Space>
                      );
                    },
                  },
                  {
                    title: 'Droit',
                    key: 'droit',
                    width: 140,
                    render: (_, row) =>
                      mayManage ? (
                        <Switch
                          size="small"
                          checked={row.droitActif}
                          checkedChildren="Oui"
                          unCheckedChildren="Non"
                          disabled={!selected?.actif || saving}
                          onChange={checked => toggleDroit(row, checked)}
                          aria-label={`Accès ${selected?.libelle} pour ${row.prenom} ${row.nom}`}
                        />
                      ) : (
                        <Tag color={row.droitActif ? 'green' : 'default'}>{row.droitActif ? 'Accordé' : 'Refusé'}</Tag>
                      ),
                  },
                ]}
              />
            )}
          </Spin>

          {total > 0 && (
            <Row justify="end">
              <Pagination
                current={page + 1}
                pageSize={PAGE_SIZE}
                total={total}
                onChange={nextPage => setPage(nextPage - 1)}
                showSizeChanger={false}
                showTotal={count => `${count} élève(s) dans ${selectedClass?.nom || 'la classe'}`}
                size="small"
              />
            </Row>
          )}
        </Space>
      </Modal>

      <Modal
        title={editingOption ? 'Renommer l’option' : 'Créer une option'}
        visible={optionModalOpen}
        onCancel={() => setOptionModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={optionForm} layout="vertical" onFinish={saveOption}>
          <Form.Item
            name="libelle"
            label="Nom de l’option"
            rules={[
              { required: true, whitespace: true, message: 'Saisissez un nom, par exemple Déjeuner.' },
              { max: 80, message: 'Le nom ne doit pas dépasser 80 caractères.' },
            ]}
          >
            <Input autoFocus placeholder="Ex. Déjeuner" maxLength={80} />
          </Form.Item>
          <Typography.Paragraph type="secondary">
            Le code technique est généré automatiquement. Vous n’avez pas à le saisir.
          </Typography.Paragraph>
          <Row justify="end" gutter={8}>
            <Col>
              <Button onClick={() => setOptionModalOpen(false)}>Annuler</Button>
            </Col>
            <Col>
              <Button type="primary" htmlType="submit" loading={saving}>
                {editingOption ? 'Enregistrer' : 'Créer l’option'}
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default ControleAcces;
